import { useContext, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Table, Input, InputNumber, Select, Button, Modal, Form, Tooltip, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTasks, useUpdateTask, useTransitionTaskStatus, useCreateTask } from "../../queries/tasks";
import { useProject, useProjects } from "../../queries/projects";
import { useStaffDirectory, StaffMember } from "../../queries/users";
import { useEpics } from "../../queries/epics";
import { useSprints } from "../../queries/sprints";
import { TaskProps, EpicProps, SprintProps, ProjectProps, Priority, Severity } from "../../types/interfaces";
import { SEVERITY_OPTIONS, PRIORITY_OPTIONS } from "../../utils/levels";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { ProjectNav } from "../../components/ui";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { EmptyState } from "../../components/ui/States";
import AuthContext from "../../context/AuthContext";
import styles from "./backlog.module.css";

function isReadyEligible(task: TaskProps): boolean {
  return !!task.description && task.storyPoints != null && !!task.assigneeId;
}

const saveErrorMessage = (err: unknown) =>
  err instanceof ApiError ? err.message : "Could not save the change";

/**
 * The grooming table. With a project id in the route it grooms that project's
 * backlog; without one (the global /backlog) it shows every project's backlog,
 * most severe and urgent first, so a manager can pull tickets-turned-tasks
 * into a sprint across projects.
 *
 * Original note on the grooming table: Unlike the other lists this one is *for* editing, so the
 * cells stay live inputs — the design work is making the three readiness
 * requirements legible at a glance instead of hiding them behind a disabled
 * button with no explanation.
 */
export const BacklogPage = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { userData } = useContext(AuthContext);
  const isStaff = userData?.role === "manager" || userData?.role === "agent";
  const isGlobal = !projectId;
  const [filterProject, setFilterProject] = useState<string | undefined>();
  const [severity, setSeverity] = useState<Severity | undefined>();
  const [priority, setPriority] = useState<Priority | undefined>();
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const { data: tasks, isLoading, isError, error } = useTasks({
    projectId: projectId || filterProject,
    status: "backlog",
    severity,
    priority,
    assigneeId,
    sort: "importance",
  });
  const { data: project } = useProject(projectId || "");
  const { data: projects } = useProjects({ enabled: isGlobal });
  const { data: users } = useStaffDirectory({ enabled: isStaff });
  const { data: epics } = useEpics(projectId || "");
  const { data: sprints } = useSprints();
  const updateTask = useUpdateTask(projectId);
  const transitionStatus = useTransitionTaskStatus(projectId);
  const createTask = useCreateTask(projectId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [form] = Form.useForm();
  const projectName = (id: string) => (projects || []).find((p: ProjectProps) => p._id === id)?.name ?? "—";

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tasks || []).filter((t: TaskProps) => (q ? t.title.toLowerCase().includes(q) : true));
  }, [tasks, query]);

  const readyCount = (tasks || []).filter(isReadyEligible).length;

  const handleMoveToReady = async (task: TaskProps) => {
    try {
      await transitionStatus.mutateAsync({ id: task._id, status: "ready" });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not move task to Ready");
    }
  };

  const handleCreateTask = async () => {
    const values = await form.validateFields();
    try {
      await createTask.mutateAsync({
        title: values.title,
        severity: values.severity,
        priority: values.priority,
        ...(isGlobal ? { projectId: values.projectId } : {}),
      });
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the task");
    }
  };

  const Readiness = ({ task }: { task: TaskProps }) => {
    const checks: [string, boolean][] = [
      ["Description", !!task.description],
      ["Points", task.storyPoints != null],
      ["Assignee", !!task.assigneeId],
    ];
    const done = checks.filter(([, ok]) => ok).length;
    return (
      <Tooltip title={checks.filter(([, ok]) => !ok).map(([l]) => l).join(", ") || "Ready to move"}>
        <span className={styles.readiness}>
          {checks.map(([label, ok]) => (
            <span key={label} className={`${styles.pip} ${ok ? styles.pipOn : ""}`} />
          ))}
          <span className="osi-num">{done}/3</span>
        </span>
      </Tooltip>
    );
  };

  return (
    <PageShell
      kind="list"
      kicker={isGlobal ? "All projects" : undefined}
      crumbs={isGlobal ? undefined : [{ label: "Projects", to: "/projects" }, { label: project?.name || "Project" }]}
      title="Backlog"
      meta={[
        { label: "Waiting", value: <span className="osi-num">{(tasks || []).length}</span> },
        { label: "Ready to move", value: <span className="osi-num" style={{ color: readyCount ? "var(--osi-ok)" : undefined }}>{readyCount}</span> },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>New task</Button>}
      tabs={isGlobal ? undefined : <ProjectNav projectId={projectId!} active="backlog" />}
    >
      <div className={styles.hint}>
        <span className={styles.hintKey}>Grooming</span>
        <span>A task needs a description, story points and an assignee before it can move to Ready.</span>
      </div>

      <Toolbar count={rows.length} unit="tasks">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--osi-text-faint)" }} />}
          placeholder="Search titles"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        {isGlobal && (
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="All projects"
            value={filterProject}
            onChange={setFilterProject}
            options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          />
        )}
        <Select allowClear style={{ width: 150 }} placeholder="Any severity" value={severity} onChange={setSeverity} options={SEVERITY_OPTIONS} />
        <Select allowClear style={{ width: 150 }} placeholder="Any priority" value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 170 }}
          placeholder="Any assignee"
          value={assigneeId}
          onChange={setAssigneeId}
          options={(users || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<TaskProps>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          /* fixed columns total 840px; without this the flexible Description
             column collapses on a 1280px screen once the sidebar is out */
          scroll={{ x: isGlobal ? 1750 : 1600 }}
          locale={{
            emptyText: (
              <EmptyState
                title="The backlog is empty"
                description="Add a task here, or promote a support ticket — either way it lands in this list to be groomed."
                actions={<Button type="primary" onClick={() => setIsModalOpen(true)}>New task</Button>}
              />
            ),
          }}
          columns={[
            {
              title: "Title", dataIndex: "title", width: 260,
              render: (title: string, task) => (
                <span>
                  <span style={{ fontWeight: 500 }}>{title}</span>
                  {task.ticketId && (
                    <Link to={`/support/tickets/${task.ticketId}`} style={{ marginLeft: 8, fontSize: 12 }}>
                      From ticket
                    </Link>
                  )}
                </span>
              ),
            },
            ...(isGlobal
              ? [{
                  title: "Project", dataIndex: "projectId", width: 160,
                  render: (id: string) => <span style={{ color: "var(--osi-text-muted)" }}>{projectName(id)}</span>,
                }]
              : []),
            {
              title: "Severity", dataIndex: "severity", width: 130,
              render: (value: Severity, task) => (
                <Select
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  defaultValue={value}
                  options={SEVERITY_OPTIONS}
                  onChange={(next) => updateTask.mutate(
                    { id: task._id, data: { severity: next } },
                    { onError: (err) => message.error(saveErrorMessage(err)) }
                  )}
                />
              ),
            },
            {
              title: "Description", dataIndex: "description",
              render: (value, task) => (
                <Input
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  placeholder="What does done look like?"
                  defaultValue={value}
                  onBlur={(e) =>
                    updateTask.mutate(
                      { id: task._id, data: { description: e.target.value } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    )
                  }
                />
              ),
            },
            {
              title: "Priority", dataIndex: "priority", width: 120,
              render: (value: Priority, task) => (
                <Select
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  defaultValue={value}
                  options={PRIORITY_OPTIONS}
                  onChange={(next) => updateTask.mutate(
                    { id: task._id, data: { priority: next } },
                    { onError: (err) => message.error(saveErrorMessage(err)) }
                  )}
                />
              ),
            },
            ...(isGlobal ? [] : [{
              title: "Epic", dataIndex: "epicId", width: 160,
              render: (value: string | null, task: TaskProps) => (
                <Select
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="No epic"
                  defaultValue={value ?? undefined}
                  options={(epics || []).map((epic: EpicProps) => ({ label: epic.title, value: epic._id }))}
                  onChange={(epicId) => updateTask.mutate(
                    { id: task._id, data: { epicId: epicId || null } },
                    { onError: (err) => message.error(saveErrorMessage(err)) }
                  )}
                />
              ),
            }]),
            {
              title: "Sprint", dataIndex: "sprintId", width: 160,
              render: (value, task) => (
                <Select
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="No sprint"
                  defaultValue={value ?? undefined}
                  options={(sprints || []).filter((sprint: SprintProps) => sprint.status !== "completed").map(
                    (sprint: SprintProps) => ({ label: sprint.name, value: sprint._id })
                  )}
                  onChange={(sprintId) => updateTask.mutate(
                    { id: task._id, data: { sprintId: sprintId || null } },
                    { onError: (err) => message.error(saveErrorMessage(err)) }
                  )}
                />
              ),
            },
            {
              title: "Points", dataIndex: "storyPoints", width: 90,
              render: (value, task) => (
                <InputNumber
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  min={0}
                  defaultValue={value ?? undefined}
                  onBlur={(e) => {
                    const parsed = Number(e.target.value);
                    updateTask.mutate(
                      { id: task._id, data: { storyPoints: e.target.value === "" || Number.isNaN(parsed) ? null : parsed } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    );
                  }}
                />
              ),
            },
            {
              title: "Assignee", dataIndex: "assigneeId", width: 170,
              render: (value, task) => (
                <Select
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  placeholder="Unassigned"
                  defaultValue={value ?? undefined}
                  options={(users || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))}
                  onChange={(v) =>
                    updateTask.mutate(
                      { id: task._id, data: { assigneeId: v || null } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    )
                  }
                />
              ),
            },
            { title: "Ready", width: 90, render: (_, task) => <Readiness task={task} /> },
            {
              title: "", width: 130, align: "right",
              render: (_, task) => (
                <Button size="small" disabled={!isReadyEligible(task)} onClick={() => handleMoveToReady(task)}>
                  Move to Ready
                </Button>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title="New task"
        open={isModalOpen}
        onOk={handleCreateTask}
        okText="Add to backlog"
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={createTask.isPending}
      >
        <Form form={form} layout="vertical">
          {isGlobal && (
            <Form.Item name="projectId" label="Project" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Pick a project"
                options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
              />
            </Form.Item>
          )}
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Short, action-shaped" />
          </Form.Item>
          <Form.Item name="severity" label="Severity" initialValue="minor">
            <Select options={SEVERITY_OPTIONS} />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
